import * as React from 'react';
import { useDrag, useDrop } from 'react-dnd';

let ITEM_TYPE = 'CustomerImageCard';

interface DragItem {
    id: number;
    index: number;
}

type UseImageCardDragAndDropParams = {
    imageId: number;
    index: number;
    isSelecting: boolean;
    isDraggable: boolean;
    moveImage: (dragIndex: number, hoverIndex: number) => void;
    onDragEnd: () => void;
};

export function useImageCardDragAndDrop({ imageId, index, isSelecting, isDraggable, moveImage, onDragEnd }: UseImageCardDragAndDropParams) {
    let ref = React.useRef<HTMLDivElement>(null);

    let [{ isDragging }, drag, preview] = useDrag({
        type: ITEM_TYPE,
        item: function () {
            return { id: imageId, index };
        },
        canDrag: function () {
            return isDraggable && !isSelecting;
        },
        collect: function (monitor) {
            return {
                isDragging: monitor.isDragging()
            };
        },
        end: function () {
            onDragEnd();
        }
    });

    let [{ isOver, canDrop }, drop] = useDrop({
        accept: ITEM_TYPE,
        canDrop: function () {
            return isDraggable;
        },
        hover: function (item: DragItem, monitor) {
            if (!isDraggable) {
                return;
            }

            if (!ref.current) {
                return;
            }

            let dragIndex = item.index;
            let hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            let hoverBoundingRect = ref.current.getBoundingClientRect();

            // Get horizontal middle
            let hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;

            // Determine mouse position
            let clientOffset = monitor.getClientOffset();
            if (!clientOffset) {
                return;
            }

            // Get pixels to the left edge
            let hoverClientX = clientOffset.x - hoverBoundingRect.left;

            // Only perform the move when the mouse has crossed half of the item's width
            // When dragging right, only move when cursor is past 50%
            // When dragging left, only move when cursor is before 50%
            if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
                return;
            }
            if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
                return;
            }

            moveImage(dragIndex, hoverIndex);

            // Mutate the monitor item to avoid repeated index lookups
            item.index = hoverIndex;
        },
        collect: function (monitor) {
            return {
                isOver: monitor.isOver(),
                canDrop: monitor.canDrop()
            };
        }
    });

    // Connect drop ref to the card, drag ref to the handle
    drop(preview(ref));

    return {
        ref,
        drag,
        isDragging,
        isOver,
        canDrop
    };
}
